import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash-es';

import { getGQLAPI, useStore } from '@mcn-platform/models';
import { GRAPHQL_AUTH_MODE } from '@aws-amplify/api-graphql';

import { search as searchQuery } from 'graphql/queries';

/** Search types supported by the pgvector knowledge_base. */
const SEARCH_TYPES = [
  'Course',
  'Document',
  'Instructor',
  'Meeting',
  'Microcast',
  'Program',
  'DiscussionPost',
  'DiscussionTopic',
  'MeetingRegistration',
  'PortalKicker',
  'Product',
  'Category',
  'WebPage',
] as const;

type SearchResultTypename = (typeof SEARCH_TYPES)[number];

type SearchResultItem = {
  type: SearchResultTypename;
  item: Record<string, unknown>;
  displayName: string;
  path: string;
};

function getItemKey(item: Record<string, unknown>, orgId: string): string {
  const id = (item.id ?? item.external_id) as string | undefined;
  const organizationID = (item.organizationID ?? item.organization_id) as string | undefined;
  if (!id) return '';
  if (organizationID) {
    return `${organizationID}~${id}`;
  }
  return orgId ? `${orgId}~${id}` : id;
}

function getEditorPath(
  type: SearchResultTypename,
  item: Record<string, unknown>,
  orgId: string,
): string {
  const key = getItemKey(item, orgId);
  const id = (item.id ?? item.external_id) as string | undefined;
  if (!key && !id) return '';

  switch (type) {
    case 'Course':
      return `/app/course-editor/${key}`;
    case 'Document':
      return `/app/document-editor/${key}`;
    case 'Instructor':
      return `/app/instructor/${id || key}`;
    case 'Meeting':
      return `/app/meeting/${key}`;
    case 'Microcast':
      return `/app/microcast-editor/${id || key}`;
    case 'Program':
      return `/app/program-editor/${key}`;
    case 'DiscussionPost':
    case 'DiscussionTopic':
      return `/app/discussion/${key}`;
    case 'MeetingRegistration':
      return `/app/meeting/${(item.meetingID ?? item.meeting_id) as string || ''}`;
    case 'PortalKicker':
      return `/app/kicker-editor/${id || key}`;
    case 'Product':
      return `/app/product-editor/${key}`;
    case 'Category':
      return `/app/category-editor/${key}`;
    case 'WebPage':
      return `/app/web-page-editor/${key}`;
    default:
      return '';
  }
}

function getDisplayName(item: Record<string, unknown>): string {
  return (
    (item.name as string) ??
    (item.headline as string) ??
    (item.title as string) ??
    (item.sku as string) ??
    (item.content as string) ??
    (item.description as string) ??
    (item.url as string) ??
    'Untitled'
  );
}

function formatErrorForDisplay(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  if (Array.isArray(err)) {
    const msgs = err
      .map((e) => (e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : null))
      .filter(Boolean);
    if (msgs.length) return msgs.join(' ');
  }
  if (err && typeof err === 'object') {
    if ('errors' in err && Array.isArray((err as { errors: unknown[] }).errors)) {
      return formatErrorForDisplay((err as { errors: unknown[] }).errors);
    }
    if ('message' in err && typeof (err as { message: unknown }).message === 'string') {
      return (err as { message: string }).message;
    }
  }
  return 'Search failed';
}

type SearchDialogProps = {
  open: boolean;
  onClose: () => void;
};

const SearchDialog = ({ open, onClose }: SearchDialogProps) => {
  const store = useStore();
  const navigate = useNavigate();
  const orgId = store.currentOrganization?.id ?? '';

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResultItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextToken, setNextToken] = useState<string | undefined>();
  const [total, setTotal] = useState<number | undefined>();
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [open]);

  const fetchSearchPage = useCallback(
    async (text: string, from: number, append: boolean) => {
      if (!text.trim() || !orgId) return;

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setError(null);
      }
      try {
        const result: any = await getGQLAPI().graphql({
          query: searchQuery,
          variables: {
            input: {
              text: text.trim(),
              filter: {
                organizationID: orgId,
                includeDoNotIndex: true,
              },
              limit: 10,
              from,
            },
          },
          authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });

        if (result?.errors?.length) {
          console.error('Search API error:', result.errors, '\nFull response:', result);
          setError(formatErrorForDisplay(result.errors));
          if (!append) setResults([]);
          return;
        }

        const rawItems = result?.data?.search?.items ?? [];
        const searchTotal = result?.data?.search?.total ?? 0;
        const searchNextToken = result?.data?.search?.nextToken ?? undefined;

        const pageResults = rawItems
          .map((raw: string | Record<string, unknown>) => {
            const item =
              typeof raw === 'string' ? (JSON.parse(raw) as Record<string, unknown>) : raw;
            const type = (item.__typename ?? '') as SearchResultTypename;
            if (!type || !SEARCH_TYPES.includes(type)) return null;
            const path = getEditorPath(type, item, orgId);
            if (!path) return null;
            return {
              type,
              item,
              displayName: getDisplayName(item),
              path,
            };
          })
          .filter(Boolean) as SearchResultItem[];

        setResults((prev) => (append ? [...prev, ...pageResults] : pageResults));
        setNextToken(searchNextToken);
        setTotal(searchTotal);
      } catch (err) {
        console.error('Search API error:', err);
        setError(formatErrorForDisplay(err));
        if (!append) setResults([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [orgId],
  );

  const runSearch = useCallback(
    (text: string) => {
      if (!text.trim() || !orgId) {
        setResults([]);
        setNextToken(undefined);
        setTotal(undefined);
        setError(null);
        return;
      }
      fetchSearchPage(text, 0, false);
    },
    [orgId, fetchSearchPage],
  );

  const loadMore = useCallback(() => {
    if (!query.trim() || !nextToken || loadingMore || loading) return;
    const from = parseInt(nextToken, 10);
    if (Number.isNaN(from)) return;
    fetchSearchPage(query, from, true);
  }, [query, nextToken, loadingMore, loading, fetchSearchPage]);

  const debouncedSearchRef = useRef(
    debounce((text: string) => runSearch(text), 300),
  );

  useEffect(() => () => debouncedSearchRef.current.cancel(), []);

  useEffect(() => {
    if (!nextToken || loadingMore || loading || !sentinelRef.current || !scrollContainerRef.current) return;
    const root = scrollContainerRef.current;
    const sentinel = sentinelRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { root, rootMargin: '100px', threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [nextToken, loadingMore, loading, loadMore]);

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    debouncedSearchRef.current(value);
  };

  const handleResultClick = (path: string) => {
    if (path) {
      navigate(path);
      onClose();
    }
  };

  const handleClose = () => {
    setQuery('');
    setResults([]);
    setError(null);
    setNextToken(undefined);
    setTotal(undefined);
    debouncedSearchRef.current.cancel();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { mt: 6 },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SearchIcon color="action" />
        <Typography variant="h6" component="span">Search</Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ ml: 'auto' }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <TextField
          inputRef={inputRef}
          fullWidth
          placeholder="Search courses, documents, microcasts..."
          value={query}
          onChange={handleQueryChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
        {loading && results.length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        )}
        {(results.length > 0 || !loading) && (
          <Box
            ref={scrollContainerRef}
            sx={{
              maxHeight: 320,
              overflowY: 'auto',
            }}
          >
            <List dense disablePadding>
              {error && (
                <Typography variant="body2" color="error" sx={{ py: 2, textAlign: 'center', px: 2 }}>
                  {error}
                </Typography>
              )}
              {!error && results.length === 0 && query.trim() && !loading && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  No results found
                </Typography>
              )}
              {results.map((r, idx) => (
                <ListItemButton
                  key={`${r.type}-${r.item.id ?? r.item.external_id ?? idx}`}
                  onClick={() => handleResultClick(r.path)}
                  disabled={!r.path}
                >
                  <ListItemText
                    primary={r.displayName}
                    secondary={r.type}
                    primaryTypographyProps={{ noWrap: true }}
                  />
                </ListItemButton>
              ))}
              {!error && nextToken && results.length > 0 && (
                <Box ref={sentinelRef} sx={{ display: 'flex', justifyContent: 'center', py: 2, minHeight: 40 }}>
                  {loadingMore && <CircularProgress size={24} />}
                </Box>
              )}
            </List>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SearchDialog;
